/**
 * Cognito Hosted UI (OAuth2 + PKCE) for federated sign-in (e.g. Google).
 * Requires the app client to allow the SPA callback URL and identity provider Google.
 */

const PKCE_VERIFIER_KEY = 'stepflow_oauth_pkce_verifier';
const OAUTH_STATE_KEY = 'stepflow_oauth_state';
const REDIRECT_URI_KEY = 'stepflow_oauth_redirect_uri';

function base64UrlEncode(buffer) {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i += 1) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function generatePkcePair() {
  const verifier = base64UrlEncode(crypto.getRandomValues(new Uint8Array(32)));
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  const challenge = base64UrlEncode(new Uint8Array(hash));
  return { verifier, challenge };
}

function randomState() {
  return base64UrlEncode(crypto.getRandomValues(new Uint8Array(16)));
}

/** Cognito hosted UI domain host only, e.g. stepflow-dev.auth.us-east-1.amazoncognito.com */
export function getCognitoOAuthDomain() {
  return (import.meta.env.VITE_COGNITO_DOMAIN || '').trim();
}

/** Must match an allowed callback URL on the Cognito app client exactly. */
export function getOAuthRedirectUri() {
  const explicit = (import.meta.env.VITE_OAUTH_REDIRECT_URI || '').trim();
  if (explicit) return explicit;
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/callback`;
  }
  return '';
}

export function isGoogleOAuthConfigured() {
  const pool = import.meta.env.VITE_COGNITO_USER_POOL_ID;
  const client = import.meta.env.VITE_COGNITO_CLIENT_ID;
  const domain = getCognitoOAuthDomain();
  return !!(pool && client && domain);
}

/**
 * Redirect browser to Cognito /oauth2/authorize (Google IdP).
 * Call after initializeCognitoConfig() so env/localStorage client id is available.
 */
export async function startGoogleHostedUISignIn() {
  return startHostedUISignIn('Google');
}

/** Hosted UI + PKCE for ANY federated provider on the pool — Google, or a company's Entra / Okta / SAML / OIDC
 *  (LOGIN-TO-TENANT.md §5). ``provider`` = the Cognito provider name. */
export async function startHostedUISignIn(provider) {
  if (!isGoogleOAuthConfigured()) {
    throw new Error('Federated sign-in is not configured (set VITE_COGNITO_DOMAIN and pool/client).');
  }
  if (!provider) throw new Error('No sign-in provider given.');
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
  const domain = getCognitoOAuthDomain();
  const redirectUri = getOAuthRedirectUri();
  if (!redirectUri) {
    throw new Error('Could not resolve OAuth redirect URI.');
  }

  const { verifier, challenge } = await generatePkcePair();
  const state = randomState();
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(OAUTH_STATE_KEY, state);
  sessionStorage.setItem(REDIRECT_URI_KEY, redirectUri);

  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    // aws.cognito.signin.user.admin — must match app client allowed scopes; needed for GetUser after OAuth
    scope: 'openid email profile aws.cognito.signin.user.admin',
    redirect_uri: redirectUri,
    identity_provider: provider,
    code_challenge_method: 'S256',
    code_challenge: challenge,
    state,
  });

  const url = `https://${domain}/oauth2/authorize?${params.toString()}`;
  window.location.assign(url);
}

/**
 * Exchange authorization code for tokens at POST /oauth2/token.
 */
export async function exchangeAuthorizationCodeForTokens(authorizationCode) {
  const domain = getCognitoOAuthDomain();
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
  const redirectUri = sessionStorage.getItem(REDIRECT_URI_KEY) || getOAuthRedirectUri();
  const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  if (!domain || !clientId || !codeVerifier) {
    throw new Error('OAuth session expired. Try signing in again.');
  }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: clientId,
    code: authorizationCode,
    redirect_uri: redirectUri,
    code_verifier: codeVerifier,
  });

  const res = await fetch(`https://${domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = json.error_description || json.error || res.statusText || 'Token exchange failed';
    throw new Error(msg);
  }

  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(OAUTH_STATE_KEY);
  sessionStorage.removeItem(REDIRECT_URI_KEY);

  return {
    idToken: json.id_token,
    accessToken: json.access_token,
    refreshToken: json.refresh_token,
  };
}

export function getStoredOAuthState() {
  return sessionStorage.getItem(OAUTH_STATE_KEY);
}

export function clearOAuthSessionStorage() {
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(OAUTH_STATE_KEY);
  sessionStorage.removeItem(REDIRECT_URI_KEY);
}


/** EMAIL-FIRST SSO (LOGIN-TO-TENANT.md §5): which company sign-in does this email use? → provider names ([] = none). */
export async function lookupCompanySignIn(apiBase, email) {
  try {
    const r = await fetch(`${apiBase}/auth/sso?email=${encodeURIComponent((email || '').trim())}`);
    if (!r.ok) return [];
    const d = await r.json();
    return Array.isArray(d.providers) ? d.providers : [];
  } catch {
    return [];
  }
}
